import { autocomplete } from "@algolia/autocomplete-js";
import "@algolia/autocomplete-theme-classic";
import { meilisearchAutocompleteClient } from "@meilisearch/autocomplete-client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Form,
  InputGroup,
  Nav,
  NavDropdown,
  Navbar,
  Placeholder,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import { Link, NavLink, useNavigate } from "react-router-dom";

import { useSession } from "../../contexts/SessionContext";
import { PermissionComponent, validateUserPermission } from "../../services/PermissionGuard";
import ShortcutService from "../../services/ShortcutService";
import translate from "../../services/Translate";
import DstButton from "../DstButton/DstButton";
import Popup from "../Popup/Popup";
import LanguageSelector from "../language-selector/LanguageSelector";
import { getMeilisearchSources, saveResultInHistory } from "./DstHeader.function";
import "./DstHeader.scss";

const DstHeader = ({ language }: { language: string }) => {
  const { first_name, handleLogout, loaded, user_id, theme, switchTheme, permissions } = useSession();
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [pageName, setPageName] = useState("");
  const [showToast, setShowToast] = useState(false);

  const _openModal = () => {
    setPageName(document.title);
    setShowModal(true);
  };

  const _saveUrl = () => {
    const currentUrl = window.location.pathname + window.location.search;
    const savedUrls: { name: string; url: string }[] = JSON.parse(localStorage.getItem("nexus-saved-urls") || "[]");

    const newEntry = { name: pageName.trim(), url: currentUrl };

    const existingIndex = savedUrls.findIndex((entry) => entry.url === currentUrl);
    if (existingIndex !== -1) {
      savedUrls[existingIndex] = newEntry;
    } else {
      savedUrls.push(newEntry);
    }

    localStorage.setItem("nexus-saved-urls", JSON.stringify(savedUrls));
    setShowModal(false);
    setShowToast(true);
  };

  const navLinks = useMemo(
    () => [
      {
        permission: [{ methods: ["GET"], route: "/roles" }],
        target: "/permissions",
        icon: "dst-icon-shield-03",
        label: translate(language, "COMPONENTS.HEADER.ADMIN"),
      },
      { target: "/users", icon: "dst-icon-folder-lock", label: translate(language, "COMPONENTS.HEADER.TRAIN") },
      {
        permission: [{ methods: ["GET"], route: "/insights/users" }],
        target: "/insights",
        icon: "dst-icon-data",
        label: translate(language, "COMPONENTS.HEADER.INSIGHTS"),
      },
      {
        permission: [{ methods: ["GET"], route: "/evaluations/attempts" }],
        target: "/evaluations/attempts?status=CORRECTION_PENDING,PRE_CORRECTED",
        icon: "dst-icon-graduation-hat-02",
        label: translate(language, "COMPONENTS.HEADER.ATTEMPTS"),
      },
      {
        permission: [{ methods: ["GET"], route: "/cursus" }],
        target: "/invitations",
        icon: "dst-icon-image-user-plus",
        label: translate(language, "COMPONENTS.HEADER.INVITATION"),
      },
      {
        permission: [{ methods: ["GET"], route: "/ephemeral-evaluations" }],
        target: "/ephemeral/sessions",
        icon: "dst-icon-file-question-02",
        label: translate(language, "COMPONENTS.HEADER.EPHEMERAL"),
      },
      {
        permission: [{ methods: ["GET"], route: "/meetings" }],
        target: "/meetings",
        icon: "dst-icon-clock",
        label: translate(language, "COMPONENTS.HEADER.MEETING"),
      },
      {
        permission: [{ methods: ["GET"], route: "/documents/diplomas" }],
        target: "/diplomas",
        icon: "dst-icon-paperclip",
        label: translate(language, "COMPONENTS.HEADER.DIPLOMAS"),
      },
      {
        permission: [{ methods: ["GET"], route: "/projects" }],
        target: "/projects",
        icon: "dst-icon-globe-02",
        label: translate(language, "COMPONENTS.HEADER.PROJECT"),
      },
      {
        permission: [{ methods: ["GET"], route: "/messages" }],
        target: "/messages",
        icon: "dst-icon-mail-02",
        label: translate(language, "COMPONENTS.HEADER.MESSAGES"),
      },
      {
        permission: [{ methods: ["GET"], route: "/demos/records" }],
        target: "/demo/records",
        icon: "dst-icon-database-01",
        label: translate(language, "COMPONENTS.HEADER.DEV"),
      },
      {
        permission: [{ methods: ["GET"], route: "/landing-page/job-blocks" }],
        target: "/landing-page/job-blocks",
        icon: "dst-icon-code-browser",
        label: translate(language, "COMPONENTS.HEADER.LANDING_PAGE"),
      },
    ],
    [language]
  );

  useEffect(() => {
    if (!containerRef.current || !validateUserPermission([{ methods: ["GET"], route: "/search" }], permissions)) return;

    const searchClient = meilisearchAutocompleteClient({
      url: process.env.REACT_APP_MEILISEARCH_URL!,
      apiKey: process.env.REACT_APP_MEILISEARCH_API_KEY,
    });

    const shortcutDestructor = ShortcutService.listen(
      "k",
      () => containerRef.current?.getElementsByTagName("input")?.[0].focus(),
      true
    );

    const searchCore = autocomplete({
      container: containerRef.current!,
      placeholder: translate(language, "COMPONENTS.HEADER.SEARCH_PLACEHOLDER").replace(
        "{{control}}",
        (navigator?.platform || (navigator as any)?.userAgentData?.platform).toUpperCase().includes("MAC")
          ? "⌘"
          : "Ctrl"
      ),
      defaultActiveItemId: 0,
      getSources: ({ query }: { query: string }) => getMeilisearchSources(searchClient, query, !query),
      openOnFocus: true,
      navigator: {
        navigate({ itemUrl, item }) {
          saveResultInHistory(itemUrl, item);
          window.location.assign(itemUrl);
        },
        navigateNewTab({ itemUrl, item }) {
          saveResultInHistory(itemUrl, item);
          window.open(itemUrl, "_blank", "noopener")?.focus();
        },
        navigateNewWindow({ itemUrl, item }) {
          saveResultInHistory(itemUrl, item);
          window.open(itemUrl, "_blank", "noopener");
        },
      },
    });

    return () => {
      shortcutDestructor();
      searchCore.destroy();
    };
  }, [language, permissions]);

  return (
    <Navbar expand="xl" id="navbar">
      <Container fluid>
        <Navbar.Brand as={Link} to="/">
          <img src="/assets/images/nexus.svg" alt="DataScientest" height="30px" className="mb-1" />
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        <Navbar.Collapse id="navbar-nav">
          <Nav className="align-items-center xs-bold w-100 flex-wrap">
            {navLinks.map(({ permission, target, icon, label }) =>
              permission ? (
                <PermissionComponent key={target} neededPermission={permission}>
                  <NavLink className="d-flex align-items-center me-2 me-xl-3" to={target}>
                    <span className={`${icon} me-1`} />
                    {label}
                  </NavLink>
                </PermissionComponent>
              ) : (
                <NavLink key={target} className="d-flex align-items-center me-2 me-xl-4" to={target}>
                  <span className={`${icon} me-1`} />
                  {label}
                </NavLink>
              )
            )}
          </Nav>
          <Nav>
            <div className="d-flex align-items-center">
              <InputGroup className="me-2">
                <div id="searcher-container" ref={containerRef}></div>
              </InputGroup>
              <NavDropdown
                title={
                  loaded ? (
                    first_name
                  ) : (
                    <Placeholder animation="glow">
                      <Placeholder xs={12} />
                    </Placeholder>
                  )
                }
                id="basic-nav-dropdown"
                align="end"
              >
                <NavDropdown.Item className="xs-bold" onClick={() => loaded && navigate(`/user/${user_id}`)}>
                  {loaded ? (
                    <React.Fragment>
                      <span className="dst-icon-image-user me-1" />
                      {translate(language, "COMPONENTS.HEADER.MY_ACCOUNT")}
                    </React.Fragment>
                  ) : (
                    <Placeholder as="span" animation="glow">
                      <Placeholder xs={6} />
                    </Placeholder>
                  )}
                </NavDropdown.Item>
                <NavDropdown.Divider />
                {/* <NavDropdown.Item href="#action/3.1" className="xs-bold">
                  <span className="dst-icon-settings-01 me-1" /> {translate(language, "COMPONENTS.HEADER.SETTINGS")}
                </NavDropdown.Item>
                <NavDropdown.Divider /> */}
                <NavDropdown.Item className="xs-bold" onClick={_openModal}>
                  <span className="dst-icon-bookmark me-1" />
                  {translate(language, "COMPONENTS.HEADER.ADD_FAVORITES.TITLE")}
                </NavDropdown.Item>
                <NavDropdown.Divider />
                <LanguageSelector className="ms-2" fontColor="black-font" />
                <NavDropdown.Divider />
                <NavDropdown.Item className="xs-bold" onClick={() => switchTheme(theme === "light" ? "dark" : "light")}>
                  <span className={`me-2 silvergray-font ${theme === "light" ? "dst-icon-moon-01" : "dst-icon-sun"}`} />
                  {theme === "light" ? "Dark" : "Light"} mode
                </NavDropdown.Item>
                <NavDropdown.Divider />
                <NavDropdown.Item className="xs-bold" onClick={() => handleLogout()}>
                  <span className="dst-icon-log-out-01 me-1" /> {translate(language, "COMPONENTS.HEADER.LOGOUT")}
                </NavDropdown.Item>
              </NavDropdown>
            </div>
          </Nav>
        </Navbar.Collapse>
      </Container>
      <Popup
        show={showModal}
        onClose={() => setShowModal(false)}
        modalTitle={translate(language, "COMPONENTS.HEADER.ADD_FAVORITES.MODAL.TITLE")}
        footerContent={
          <React.Fragment>
            <DstButton
              value={translate(language, "COMPONENTS.HEADER.ADD_FAVORITES.MODAL.BUTTON_CANCEL")}
              variant="secondary"
              clickFunction={() => setShowModal(false)}
            />
            <DstButton
              value={translate(language, "COMPONENTS.HEADER.ADD_FAVORITES.MODAL.BUTTON_CONFIRM")}
              clickFunction={_saveUrl}
              disabled={!pageName.trim()}
            />
          </React.Fragment>
        }
      >
        <Form onSubmit={(event) => event.preventDefault()}>
          <Form.Group>
            <Form.Label>{translate(language, "COMPONENTS.HEADER.ADD_FAVORITES.MODAL.LABEL")}</Form.Label>
            <Form.Control
              type="text"
              value={pageName}
              onChange={(event) => setPageName(event.target.value)}
              placeholder={translate(language, "COMPONENTS.HEADER.ADD_FAVORITES.MODAL.PLACEHOLDER")}
              autoFocus
            />
          </Form.Group>
        </Form>
      </Popup>
      <ToastContainer position="bottom-end" className="p-3">
        <Toast bg="success" show={showToast} onClose={() => setShowToast(false)} delay={1500} autohide>
          <Toast.Body className="text-white">
            {translate(language, "COMPONENTS.HEADER.ADD_FAVORITES.SUCCESS_SAVE")}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </Navbar>
  );
};

export default DstHeader;
